import React, { Suspense } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from './components/Layout';
import ScrollOnNavigate from './components/ScrollOnNavigate';
import { SystemProvider } from './context/SystemContext';
import { AuthProvider } from './context/AuthContext';
import { FavoritesProvider } from './context/FavoritesContext';
import EntityRoute from './components/EntityRoute';
import LegacyDetailRedirect from './components/LegacyDetailRedirect';
import Home from './pages/Home';
import genericFamily from './pages/generic';
import { getFamily, familyElsewhere } from './config/familyRegistry';
import { getActiveSystem } from './config/system';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';
import FamilyElsewhere from './pages/FamilyElsewhere';
// Tiny and eager: the v1 sheet editor's addresses redirect to the builder (issue 934).
import SheetBuilderRedirect from './pages/authoring/SheetBuilderRedirect';
// A lazy page reloads once when its chunk is gone after a deploy (issue 953).
import lazyPage from './utils/lazyPage';

// ROUTE-LEVEL CODE SPLITTING (measured 2026-09-07). The reference pages —
// Home, every Family's list/detail/compare, sign-in — stay in the main chunk:
// they are what nearly every visit is for. Everything else loads on first
// navigation: the generators, the tracker, the account and campaign pages,
// the guides and licence text, the admin queues and the whole authoring suite
// were a third of a single 1 MB script that a visitor reading one spell had
// to download. The worker caches each hashed chunk cache-first once fetched,
// so a lazily loaded page is as available offline as it was before.
const Favorites = lazyPage(() => import('./pages/Favorites'));
const DmScreen = lazyPage(() => import('./pages/DmScreen'));
const Characters = lazyPage(() => import('./pages/Characters'));
const Campaigns = lazyPage(() => import('./pages/Campaigns'));
const CampaignWikiReader = lazyPage(() => import('./pages/CampaignWikiReader'));
const TableHandoff = lazyPage(() => import('./pages/TableHandoff'));
const DefaultTable = lazyPage(() => import('./pages/DefaultTable'));
const TestLogin = lazyPage(() => import('./pages/TestLogin'));
const Account = lazyPage(() => import('./pages/Account'));
const Privacy = lazyPage(() => import('./pages/Privacy'));
const Terms = lazyPage(() => import('./pages/Terms'));
const OGL = lazyPage(() => import('./pages/OGL'));
const ShopGenerator = lazyPage(() => import('./pages/ShopGenerator'));
const SystemsGuide = lazyPage(() => import('./pages/SystemsGuide'));
const FeaturesGuide = lazyPage(() => import('./pages/FeaturesGuide'));
const ClaimShare = lazyPage(() => import('./pages/ClaimShare'));
const TreasureGenerator = lazyPage(() => import('./pages/TreasureGenerator'));
const TrapBuilder = lazyPage(() => import('./pages/TrapBuilder'));
const InitiativeTracker = lazyPage(() => import('./pages/InitiativeTracker'));
const SharedTracker = lazyPage(() => import('./pages/SharedTracker'));
const AdminReports = lazyPage(() => import('./pages/AdminReports'));
const AdminContent = lazyPage(() => import('./pages/AdminContent'));
const AdminVerification = lazyPage(() => import('./pages/AdminVerification'));
const SharedWithMe = lazyPage(() => import('./pages/authoring/SharedWithMe'));
const MySystems = lazyPage(() => import('./pages/authoring/MySystems'));
const SystemEditor = lazyPage(() => import('./pages/authoring/SystemEditor'));
const FamilyEditor = lazyPage(() => import('./pages/authoring/FamilyEditor'));
const EntryEditor = lazyPage(() => import('./pages/authoring/EntryEditor'));
const SheetBuilder = lazyPage(() => import('./pages/authoring/SheetBuilder'));
const MyHomebrew = lazyPage(() => import('./pages/authoring/MyHomebrew'));
const LayerEditor = lazyPage(() => import('./pages/authoring/LayerEditor'));
const LayerFamilyEditor = lazyPage(() => import('./pages/authoring/LayerFamilyEditor'));
const LayerSheetEditor = lazyPage(() => import('./pages/authoring/LayerSheetEditor'));
const LayerEntries = lazyPage(() => import('./pages/authoring/LayerEntries'));
const Workbench = lazyPage(() => import('./pages/authoring/workbench/Workbench'));

// The same spinner the pages themselves show while they load their data.
const PageLoading = () => (
  <div className="flex justify-center items-center h-64">
    <div className="loading-spinner h-10 w-10" role="status" aria-label="Loading" />
  </div>
);

// The MRCS catch-all content route (05 §1.1, §6.2 — the R13 collapse). All ten
// UI Families run on the generic engine, so the route table no longer needs a
// line per Family: one dynamic segment, resolved against the Family registry.
// React Router ranks static segments above a dynamic one, so the fixed routes
// below (/tracker, /shop, /treasure, admin, auth, legal) always win regardless
// of declaration order — mounting this after them keeps the intent readable.
//
// The pair is cached per segment because genericFamily() mints new component
// identities: a fresh pair on every render would remount the page (and refetch)
// on each keystroke in a filter input.
const familyPairs = new Map();
function pairFor(segment) {
  if (!familyPairs.has(segment)) familyPairs.set(segment, genericFamily(segment));
  return familyPairs.get(segment);
}

function FamilyRoute() {
  const { familyPath } = useParams();
  // Presence is a registry question and is system-independent (canonical
  // families are shared). A canonical family WITHOUT a nav position (template,
  // archetype) has no page of its own — templates render as a detail panel and
  // archetypes inline on the class page — so those segments keep the existing
  // not-found behavior, exactly as an unknown segment does.
  // Resolved under the ACTIVE system, not the default. Every canonical family
  // exists under both built-ins, so this made no difference while all content
  // was d20 — and it is the whole question for a user-created system, whose
  // Families exist under exactly one system and are unknown to every other.
  const system = getActiveSystem();
  const family = getFamily(familyPath, system);
  if (!family || family.navOrder == null) {
    // A page only another system has (issue 973): say where it is.
    const elsewhere = familyElsewhere(familyPath, system);
    if (elsewhere) return <FamilyElsewhere segment={familyPath} system={system} elsewhere={elsewhere} />;
    return <NotFound />;
  }
  const { List, Detail, Compare } = pairFor(familyPath);
  return <EntityRoute List={List} Detail={Detail} Compare={Compare} />;
}

function App() {
  return (
    <>
      <Helmet>
        <title>The Dragon's Table - Comprehensive Tabletop RPG Reference</title>
        <meta name="description" content="Complete 3.5e & Pathfinder 1e tabletop RPG reference with spells, feats, classes, races, monsters, items, and more." />
      </Helmet>

      <AuthProvider>
      <SystemProvider>
      <FavoritesProvider>
      <Layout>
        <ScrollOnNavigate />
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          
          {/* Backward-compat: old path-based detail URLs redirect to ?slug= form.
              Rulebook-scoped form (/spells/<rulebook>/<slug>) covers the legacy
              links embedded in imported content (see LegacyDetailRedirect). */}
          <Route path="/spells/:rulebookSlug/:slug" element={<LegacyDetailRedirect base="/spells" />} />
          <Route path="/feats/:rulebookSlug/:slug" element={<LegacyDetailRedirect base="/feats" />} />
          <Route path="/classes/:rulebookSlug/:slug" element={<LegacyDetailRedirect base="/classes" />} />
          <Route path="/races/:rulebookSlug/:slug" element={<LegacyDetailRedirect base="/races" />} />
          <Route path="/monsters/:rulebookSlug/:slug" element={<LegacyDetailRedirect base="/monsters" />} />
          <Route path="/items/:rulebookSlug/:slug" element={<LegacyDetailRedirect base="/items" />} />
          <Route path="/spells/:slug" element={<LegacyDetailRedirect base="/spells" />} />
          <Route path="/feats/:slug" element={<LegacyDetailRedirect base="/feats" />} />
          <Route path="/classes/:slug" element={<LegacyDetailRedirect base="/classes" />} />
          <Route path="/races/:slug" element={<LegacyDetailRedirect base="/races" />} />
          <Route path="/monsters/:slug" element={<LegacyDetailRedirect base="/monsters" />} />
          <Route path="/items/:slug" element={<LegacyDetailRedirect base="/items" />} />
          <Route path="/skills/:slug" element={<LegacyDetailRedirect base="/skills" />} />
          <Route path="/languages/:slug" element={<LegacyDetailRedirect base="/languages" />} />
          <Route path="/deities/:slug" element={<LegacyDetailRedirect base="/deities" />} />
          <Route path="/rulebooks/:slug" element={<LegacyDetailRedirect base="/rulebooks" />} />

          {/* Account (auth is system-agnostic — an account spans game systems;
              favorites are listed per active system) */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/favorites" element={<Favorites />} />
          {/* DM screen (issue 200): a dashboard of pinned references —
              login-gated inside the page, like Favorites. */}
          <Route path="/dm-screen" element={<DmScreen />} />
          {/* Characters (M1): the locker, and one character at ?id=. Query
              params only, like every other page — `characters` is a reserved
              url_segment, so no Family can claim the route. */}
          <Route path="/characters" element={<Characters />} />
          {/* Campaigns (Parties C1, issue 320): the list, and one campaign's
              parties at ?id=. Query params only — `campaigns` is a reserved
              url_segment, so no Family can claim the route. */}
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/wiki" element={<CampaignWikiReader />} />
          {/* The homepage card's ?go=default: the default campaign's table, else the Campaigns page (issue 1581). */}
          <Route path="/table" element={<DefaultTable />} />
          <Route path="/table/:campaignId" element={<TableHandoff />} />
          <Route path="/test-login" element={<TestLogin />} />
          <Route path="/account" element={<Account />} />

          {/* Initiative Tracker (#61): the DM view is PUBLIC and runs
              in-memory for guests — only save/share is gated in-page (#140);
              the shared player view is PUBLIC (token is the capability) */}
          <Route path="/tracker" element={<InitiativeTracker />} />
          <Route path="/tracker/shared/:token" element={<SharedTracker />} />

          {/* Admin triage (guarded in-page by useAuth().user.is_admin;
              the API itself is requireAdmin) */}
          <Route path="/admin/reports" element={<AdminReports />} />
          {/* Admin moderation of user content — publication is an admin
              action (09 §6: no self-publishing) */}
          <Route path="/admin/content" element={<AdminContent />} />
          <Route path="/admin/verification" element={<AdminVerification />} />

          {/* Authoring (MRCS Phase D): a person's own game systems, their
              Families and their Entries. Guarded in-page by useAuth(); every
              API route behind it is requireAuth AND owner-checked, so these
              paths carry no authority of their own. Declared before the
              content catch-all — `authoring` is a reserved url_segment, so no
              Family can claim it — and STATIC-first, so /authoring/:slug and
              its children rank above /:familyPath either way. */}
          <Route path="/authoring" element={<MySystems />} />
          {/* The homebrew half (D4/D5) is declared FIRST, because `homebrew` is
              a legal system slug and React Router ranks a static segment above
              a dynamic one either way — so the ordering here only states what
              the ranking already does. `POST /api/authoring/systems` refuses
              the slug outright, which is the half that actually prevents the
              collision. */}
          <Route path="/authoring/homebrew" element={<MyHomebrew />} />
          {/* The workbench (issue 1498): rules built from blocks, scoped to
              ?system=. `workbench` is a reserved system slug too. */}
          <Route path="/authoring/workbench" element={<Workbench />} />
          <Route path="/authoring/homebrew/:id" element={<LayerEditor />} />
          <Route path="/authoring/homebrew/:id/sheet/:key" element={<LayerSheetEditor />} />
          {/* A homebrew's whole copy of its system's sheet (issue 663) opens in
              the builder, saved into the layer; the v1 editor's /replace
              address redirects there (issue 934). */}
          <Route path="/authoring/homebrew/:id/sheet/:key/replace" element={<SheetBuilderRedirect layer />} />
          <Route path="/authoring/homebrew/:id/sheet/:key/build" element={<SheetBuilder layer />} />
          <Route path="/authoring/homebrew/:id/f/:key" element={<LayerFamilyEditor />} />
          <Route path="/authoring/homebrew/:id/f/:key/entries" element={<LayerEntries />} />
          <Route path="/authoring/:slug" element={<SystemEditor />} />
          <Route path="/authoring/:slug/f/:key" element={<FamilyEditor />} />
          <Route path="/authoring/:slug/f/:key/entries" element={<EntryEditor />} />
          {/* The character sheet of a system you own: the sheet builder
              (issue 871); the v1 editor's address redirects there (934). */}
          <Route path="/authoring/:slug/sheet/:key" element={<SheetBuilderRedirect />} />
          <Route path="/authoring/:slug/sheet/:key/build" element={<SheetBuilder />} />


          {/* Legal */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/ogl" element={<OGL />} />
          <Route path="/licenses" element={<OGL />} />
          <Route path="/shop" element={<ShopGenerator />} />
          {/* Treasure Hoard Generator (#145): public, stateless, in-page */}
          <Route path="/treasure" element={<TreasureGenerator />} />
          {/* Trap Builder (issue 1584): public, CR and cost from the system's trap-design tables */}
          <Route path="/trap-builder" element={<TrapBuilder />} />
          {/* The site features guide (#318). Public and top-level on purpose:
              its audience is the visitor who has not signed in. */}
          <Route path="/guide" element={<FeaturesGuide />} />
          {/* Systems & Homebrew guide (MRCS): the deeper guide, linked from
              the features guide, from MySystems and from MyHomebrew */}
          <Route path="/systems-guide" element={<SystemsGuide />} />
          {/* Sharing (MRCS 10). `/share/:token` is the URL an owner sends. */}
          <Route path="/share/:token" element={<ClaimShare />} />
          <Route path="/authoring/shared" element={<SharedWithMe />} />

          {/* Content: every UI Family, one catch-all resolved against the
              registry (?slug= selects the detail view; system/rulebook ride as
              query params — EntityRoute). A segment no navigable canonical
              family claims renders NotFound, same as the * fallback below. */}
          <Route path="/:familyPath" element={<FamilyRoute />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </Layout>
      </FavoritesProvider>
      </SystemProvider>
      </AuthProvider>
    </>
  );
}

export default App; 